import { useState } from 'react';
import { Check, Coins } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToggleTaskCompletion, WeeklyTask } from '@/hooks/useWeeklyTasks';
import { useUserIcons } from '@/hooks/useUserIcons';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { fetchCatImage } from '@/utils/catApi';

interface WeeklyTaskSlotCardProps {
  task: WeeklyTask;
  user: {
    id: string;
    name: string;
    color: string;
    textColor: string;
  } | undefined;
  hour: number | null;
  weekOffset: number;
  onEditClick?: (task: WeeklyTask) => void;
}

export function WeeklyTaskSlotCard({ task, user, hour, weekOffset, onEditClick }: WeeklyTaskSlotCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [catImage, setCatImage] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const toggleCompletion = useToggleTaskCompletion();
  const { getUserIcon } = useUserIcons();

  const handleComplete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // If task is already completed, just toggle it back (no cat)
    if (task.isCompleted) {
      toggleCompletion.mutate({
        templateId: task.id,
        scheduledDay: task.scheduled_day,
        scheduledHour: task.scheduled_hour,
        scheduledMinute: task.scheduled_minute,
        weekOffset,
        isCurrentlyCompleted: task.isCompleted,
        completionId: task.completionId,
        userName: task.user_name,
        coins: task.coins,
        taskTitle: task.title,
      });
      return;
    }
    
    // Show cat dialog for completing a task
    if (isCompleting) return;
    setIsCompleting(true);
    
    const imageUrl = await fetchCatImage();
    if (imageUrl) {
      setCatImage(imageUrl);
      setShowCatDialog(true);
    } else {
      // If cat fetch fails, still complete the task
      toggleCompletion.mutate({
        templateId: task.id,
        scheduledDay: task.scheduled_day,
        scheduledHour: task.scheduled_hour,
        scheduledMinute: task.scheduled_minute,
        weekOffset,
        isCurrentlyCompleted: task.isCompleted,
        completionId: task.completionId,
        userName: task.user_name,
        coins: task.coins,
        taskTitle: task.title,
      });
      setIsCompleting(false);
    }
  };

  const handleCatDialogClose = (open: boolean) => {
    if (!open) {
      setShowCatDialog(false);
      setIsCompleting(false);
      // Complete the task when dialog closes
      toggleCompletion.mutate({
        templateId: task.id,
        scheduledDay: task.scheduled_day,
        scheduledHour: task.scheduled_hour,
        scheduledMinute: task.scheduled_minute,
        weekOffset,
        isCurrentlyCompleted: false,
        completionId: undefined,
        userName: task.user_name,
        coins: task.coins,
        taskTitle: task.title,
      });
    }
  };

  const formattedTime = hour !== null 
    ? `${hour.toString().padStart(2, '0')}:${(task.scheduled_minute || 0).toString().padStart(2, '0')}`
    : null;

  // Get user icon
  const userIconName = user ? getUserIcon(user.id) : null;
  const UserIcon = userIconName ? (LucideIcons as any)[userIconName] : null;

  // Display English title only
  const displayTitle = task.title;

  const handleCardClick = () => {
    onEditClick?.(task);
  };

  return (
    <div
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        "mb-2 rounded-xl transition-all duration-300 overflow-hidden cursor-pointer w-full max-w-full",
        "border-2 shadow-sm",
        task.isCompleted
          ? "bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-600/50"
          : "bg-slate-700 hover:shadow-lg hover:scale-[1.02]",
        isHovered && !task.isCompleted && "ring-2 ring-offset-1 ring-offset-slate-800"
      )}
      style={{
        borderRightWidth: '4px',
        borderRightColor: user?.color,
        ...(isHovered && !task.isCompleted ? { '--tw-ring-color': user?.color } as React.CSSProperties : {})
      }}
    >
      {/* Header with Time and Checkbox */}
      <div className={cn(
        "flex items-center justify-between px-3 py-2",
        task.isCompleted 
          ? "bg-green-800/40" 
          : "bg-gradient-to-r from-slate-600/50 to-transparent"
      )}>
        {/* Time */}
        {formattedTime && (
          <div className={cn(
            "text-sm font-bold tracking-wide",
            task.isCompleted ? "text-green-400" : "text-slate-200"
          )}>
            {formattedTime}
          </div>
        )}
        {!formattedTime && <div />}
        
        {/* Checkbox */}
        <button
          onClick={handleComplete}
          disabled={toggleCompletion.isPending}
          className={cn(
            "w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300",
            task.isCompleted
              ? "bg-green-500 text-white shadow-md"
              : "bg-slate-600 border-2 border-slate-500 hover:border-green-400 hover:bg-green-900/50",
            toggleCompletion.isPending && "opacity-50"
          )}
        >
          {task.isCompleted ? (
            <Check className="w-4 h-4" strokeWidth={3} />
          ) : (
            <div className={cn(
              "w-2 h-2 rounded-full transition-all",
              isHovered ? "bg-green-400" : "bg-transparent"
            )} />
          )}
        </button>
      </div>

      {/* Separator */}
      <div 
        className="h-0.5 mx-3"
        style={{ backgroundColor: task.isCompleted ? '#86efac' : user?.color + '40' }}
      />

      {/* Task Name */}
      <div className={cn(
        "px-3 py-2 font-medium text-sm leading-tight",
        task.isCompleted 
          ? "text-green-400 line-through opacity-70" 
          : "text-slate-100"
      )}>
        {displayTitle}
      </div>

      {/* Footer with Avatar Icon and Coins */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-600/30">
        {/* User Icon */}
        <div 
          className={cn(
            "w-7 h-7 rounded-full flex items-center justify-center text-white shadow-sm",
            task.isCompleted && "opacity-60"
          )}
          style={{ backgroundColor: user?.color }}
        >
          {UserIcon ? (
            <UserIcon className="w-4 h-4" />
          ) : (
            <span className="text-xs font-bold">
              {user?.name?.charAt(0) || '?'}
            </span>
          )}
        </div>

        {/* Coins */}
        {task.coins > 0 && (
          <div className={cn(
            "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold",
            task.isCompleted
              ? "bg-green-800/50 text-green-400"
              : "bg-amber-900/50 text-amber-400"
          )}>
            <Coins className="w-3 h-3" />
            <span>{task.coins}</span>
          </div>
        )}
      </div>

      {/* Cat Celebration Dialog */}
      <Dialog open={showCatDialog} onOpenChange={handleCatDialogClose}>
        <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogTitle className="text-xl font-semibold text-center">המשימה הושלמה! 🎉</DialogTitle>
          <div className="flex flex-col items-center space-y-4">
            <p className="text-center text-muted-foreground">הנה חתול כדי לחגוג את ההישג שלך!</p>
            {catImage && (
              <img 
                src={catImage} 
                alt="Celebration Cat" 
                className="rounded-lg max-h-[300px] w-auto object-contain animate-fade-in"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
